import moment from 'moment';
import { defaultEnvironment } from '../environment';
import { mobilitySurveyBikeReasons, mobilitySurveyDemotivationReasons, mobilitySurveyTransportModes, Place } from '../models';
import { parseFMDParameters } from './fmd-parameters';
import { HttpService } from './http';
export class MobilitySurveyService {
  static async getSurveys({
    partner
  }) {
    try {
      const results = await HttpService.get('v1', `/partners/${partner.id}/mobility_surveys`, [], [], null, {
        partner: partner.code
      });
      return results.map(parseMobilitySurvey);
    } catch (err) {
      console.error('[MobilitySurveyService][getSurveys]', err);
      throw err;
    }
  }
  static async getSurvey({
    code
  }) {
    try {
      const data = await HttpService.get('v1', `/mobility_surveys/${code}`);
      return parseMobilitySurvey(data);
    } catch (err) {
      console.error('[MobilitySurveyService][getSurvey]', err);
      throw err;
    }
  }
  static async createSurvey({
    partner
  }) {
    try {
      const data = await HttpService.post('v1', `/partners/${partner.id}/mobility_surveys`, [], [], JSON.stringify({
        partner: partner.id
      }), {
        partner: partner.code
      });
      return parseMobilitySurvey(data);
    } catch (err) {
      console.error('[MobilitySurveyService][createSurvey]', err);
      throw err;
    }
  }
  static async respond({
    partnerId: partner,
    professionalEmail: professional_email,
    transportModes,
    alternativesTransportModes,
    bikeReasons,
    demotivationReasons,
    maxDurationInMinutes,
    computedRoute,
    selectedSite,
    readyToTryCycling,
    home,
    work,
    cyclingDisadvantageOther
  }) {
    try {
      const {
        employee_inscription_token: employeeToken,
        employee_created: isNewEmployee,
        cycled_distance_km_over_one_year,
        cycled_duration_over_one_year_in_seconds,
        life_expectancy_seconds_over_one_year,
        earned_euros_fmd_over_one_year: fmdAllowance,
        earned_euros_transport_savings_over_one_year: transportSavings,
        saved_co2_over_one_year_in_kg: savedCO2
      } = await HttpService.post('v1', `/employee_with_transport_habit`, [], [], JSON.stringify({
        partner,
        professional_email,
        transport_habit: {
          goes_to_work_by_bicycle: transportModes.includes('bicycle'),
          transportation_modes: transportModes.map(key => transportModesMap[key]),
          alternative_to_bike_transportation_modes: alternativesTransportModes.map(key => transportModesMap[key]),
          cycling_advantages: bikeReasons.map(key => bikeReasonsMap[key]),
          cycling_disadvantages: demotivationReasons.map(key => demotivationReasonsMap[key]),
          commute_duration_in_minutes: maxDurationInMinutes ? maxDurationInMinutes : null,
          average_commute_count_by_week: 5,
          bike_itinerary_duration_in_seconds: computedRoute?.cyclingDuration || null,
          bike_itinerary_distance_in_meters: computedRoute?.distances.total || null,
          estimated_emitted_co2_kg_per_year: null,
          home_geo_point: home?.point || null,
          home_address: home?.address || null,
          work_geo_point: work?.point || null,
          work_address: work?.address || null,
          work_site_id: selectedSite?.id || null,
          ready_to_try_cycling: readyToTryCycling,
          cycling_disadvantage_other: cyclingDisadvantageOther ? cyclingDisadvantageOther : null
        }
      }));
      return {
        employeeToken,
        isNewEmployee,
        results: {
          daysOfLifeGained: life_expectancy_seconds_over_one_year / 60 / 60 / 24,
          distancePerYear: cycled_distance_km_over_one_year * 1000,
          fmdAllowance,
          transportSavings,
          hoursCycled: cycled_duration_over_one_year_in_seconds / 60 / 60,
          savedCO2
        }
      };
    } catch (err) {
      console.error('[MobilitySurveyService][respond]', err);
      throw err;
    }
  }
  static async getStats({
    partner,
    siteId
  }) {
    try {
      const params = [];
      if (siteId) params.push({
        key: 'site_id',
        value: siteId
      });
      const data = await HttpService.get('v1', `/partners/${partner.id}/transport_habit_stats`, params, [], null, {
        partner: partner.code
      });
      return parseMobilitySurveyStats(data);
    } catch (err) {
      console.error('[MobilitySurveyService][getStats]', err);
      throw err;
    }
  }
}
export const transportModesMap = {
  bicycle: 'BIKE',
  car: 'CAR',
  carpooling: 'CAR_POOLING',
  motorbike: 'MOTORBIKE',
  publicTransport: 'PUBLIC_TRANSPORT',
  scooter: 'SCOOTER',
  walk: 'WALKING'
};
export const backendMobilitySurveyTransportModes = {
  BIKE: 'bicycle',
  CAR: 'car',
  CAR_POOLING: 'carpooling',
  MOTORBIKE: 'motorbike',
  PUBLIC_TRANSPORT: 'publicTransport',
  SCOOTER: 'scooter',
  WALKING: 'walk'
};
export const bikeReasonsMap = {
  cyclingCommunity: 'BE_PART_OF_A_CYCLING_COMMUNITY',
  fmd: 'EARN_MONEY_THROUGH_FINANCIAL_COMPENSATION',
  pleasureAndFreedom: 'PLEASURE_AND_FREEDOM',
  positiveImpactOnEnvironment: 'POSITIVE_IMPACT_ON_ENVIRONMENT',
  positiveImpactOnHealth: 'POSITIVE_IMPACT_ON_HEALTH_AND_WELL_BEING',
  practicalOrQuick: 'PRACTICAL_OR_QUICK',
  saveMoney: 'SAVE_MONEY'
};
export const backendBikeReasonsMap = {
  BE_PART_OF_A_CYCLING_COMMUNITY: 'cyclingCommunity',
  EARN_MONEY_THROUGH_FINANCIAL_COMPENSATION: 'fmd',
  PLEASURE_AND_FREEDOM: 'pleasureAndFreedom',
  POSITIVE_IMPACT_ON_ENVIRONMENT: 'positiveImpactOnEnvironment',
  POSITIVE_IMPACT_ON_HEALTH_AND_WELL_BEING: 'positiveImpactOnHealth',
  PRACTICAL_OR_QUICK: 'practicalOrQuick',
  SAVE_MONEY: 'saveMoney'
};
export const demotivationReasonsMap = {
  fearForMySafety: 'FEAR_FOR_MY_SAFETY',
  fearOfTheft: 'FEAR_OF_THEFT',
  haveADisability: 'HAVE_A_DISABILITY',
  logisticalConstraints: 'LOGISTICAL_CONSTRAINTS_EQUIPMENT_OR_CHILDREN_TO_CARRY',
  noBikeOrNotInGoodCondition: 'NO_BIKE_OR_BIKE_NOT_IN_GOOD_CONDITION',
  noEquipmentAtWork: 'NO_EQUIPMENT_IN_THE_WORKPLACE',
  notComfortableCycling: 'NOT_COMFORTABLE_CYCLING_OR_NEVER_RIDDEN_A_BIKE',
  satisfiedOfMyCurrentCommuting: 'SATISFIED_OF_MY_CURRENT_COMMUTING',
  tooMuchEffort: 'TOO_MUCH_EFFORT',
  weather: 'DONT_WANT_TO_ENDURE_WEATHER',
  other: 'OTHER_DISADVANTAGE'
};
export const backendDemotivationReasonsMap = {
  FEAR_FOR_MY_SAFETY: 'fearForMySafety',
  FEAR_OF_THEFT: 'fearOfTheft',
  HAVE_A_DISABILITY: 'haveADisability',
  LOGISTICAL_CONSTRAINTS_EQUIPMENT_OR_CHILDREN_TO_CARRY: 'logisticalConstraints',
  NO_BIKE_OR_BIKE_NOT_IN_GOOD_CONDITION: 'noBikeOrNotInGoodCondition',
  NO_EQUIPMENT_IN_THE_WORKPLACE: 'noEquipmentAtWork',
  NOT_COMFORTABLE_CYCLING_OR_NEVER_RIDDEN_A_BIKE: 'notComfortableCycling',
  SATISFIED_OF_MY_CURRENT_COMMUTING: 'satisfiedOfMyCurrentCommuting',
  TOO_MUCH_EFFORT: 'tooMuchEffort',
  DONT_WANT_TO_ENDURE_WEATHER: 'weather',
  OTHER_DISADVANTAGE: 'other'
};
function parseMobilitySurveyPartner({
  id,
  code,
  title,
  icon,
  fmd_parameters,
  geo_group
}, invitation_code) {
  return {
    id,
    code,
    title,
    icon: icon ? `${defaultEnvironment.backendUrl}${icon}` : null,
    fmdConfig: fmd_parameters ? parseFMDParameters(fmd_parameters) : null,
    geogroup: {
      id: geo_group.id,
      title: geo_group.title,
      place: new Place(-1, geo_group.geo_point, {
        primaryText: geo_group.address
      }),
      invitationCode: invitation_code
    },
    sites: (geo_group.sub_groups || []).filter(({
      sub_group_type
    }) => sub_group_type === 'COMPANY_SITE').map(({
      id,
      title,
      geo_point,
      address
    }) => ({
      id,
      title,
      place: new Place(-1, geo_point, {
        primaryText: address
      })
    }))
  };
}
function parseMobilitySurvey({
  id,
  code,
  created,
  updated,
  closed,
  partner,
  invitation_code
}) {
  return {
    id,
    code,
    created: moment(created),
    updated: moment(updated),
    closed: moment(closed),
    partner: partner && typeof partner !== 'number' ? parseMobilitySurveyPartner(partner, invitation_code) : undefined
  };
}
export function parseMobilitySurveyStats({
  has_transport_mode: respondantsCount,
  multi_transport_mode: respondantsDoingMultimodalCount,
  sum_estimated_emitted_co2_kg_per_year: totalEmittedCO2KgPerYear,
  advantages,
  disadvantages,
  transport_modes,
  commuting_time
}) {
  return {
    respondantsCount,
    respondantsDoingMultimodalCount,
    totalEmittedCO2KgPerYear: totalEmittedCO2KgPerYear ?? undefined,
    benefits: mobilitySurveyBikeReasons.reduce((res, key) => {
      res[key] = advantages[bikeReasonsMap[key]] || 0;
      return res;
    }, {}),
    benefitsRespondantsCount: advantages.respondent_count || 0,
    demotivationReasons: mobilitySurveyDemotivationReasons.reduce((res, key) => {
      res[key] = disadvantages[demotivationReasonsMap[key]] || 0;
      return res;
    }, {}),
    demotivationReasonsRespondantsCount: disadvantages.respondent_count || 0,
    transportModes: mobilitySurveyTransportModes.reduce((res, key) => {
      res[key] = transport_modes[transportModesMap[key]] || 0;
      return res;
    }, {}),
    durations: {
      15: commuting_time.less_than_15_min,
      30: commuting_time.less_than_30_min,
      45: commuting_time.less_than_45_min,
      60: commuting_time.less_than_60_min,
      more: commuting_time.more_than_60_min
    }
  };
}